"use client";

import React from "react";
import { Activity, Droplets, FlaskConical, ShieldCheck } from "lucide-react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
const chartData = [{
  time: "2021",
  recurrence: 120
}, {
  time: "2022",
  recurrence: 245
}, {
  time: "2023",
  recurrence: 380
}, {
  time: "2024",
  recurrence: 500
}];
const chartConfig = {
  recurrence: {
    label: "Portfolio Value ($M)",
    color: "hsl(var(--primary))"
  }
} satisfies ChartConfig;
const statsData = [{
  icon: Activity,
  value: "$14M+",
  label: "ASSETS MANAGED",
  desc: "Total portfolio value under our strategic oversight."
}, {
  icon: ShieldCheck,
  value: "12+",
  label: "INFILL PROJECTS",
  desc: "Successful developments delivered to urban builders."
}, {
  icon: Droplets,
  value: "50% +\xA0",
  label: "MORE YIELD",
  desc: "50% more money left\nin your profit\n"
}, {
  icon: FlaskConical,
  value: "100%",
  label: "TRANSPARENCY",
  desc: "Full reporting and data-driven market insights."
}];
export default function TheLorenzoAdvantage() {
  return <section data-section-id="222804" id="the-lorenzo-advantage" className="relative overflow-hidden bg-background py-20 lg:py-32 dark">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[50vw] w-[50vw] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] h-[40vw] w-[40vw] rounded-lg bg-secondary/10 blur-[100px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-3xl">
          <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            The Lorenzo Advantage
          </h2>
          <p className="text-muted-foreground font-default text-lg md:text-xl">
            We provide architectural sophistication and structural transparency to 
            infill builders, home buyers, and investors. Our data-driven approach 
            ensures you secure the highest value in the real estate market.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          {}
          <div className="lg:col-span-7">
            <Card className="border-border bg-card/50 text-card-foreground shadow-md backdrop-blur-xl rounded-md">
              <CardHeader className="pb-4 border-b border-border">
                <CardTitle className="font-default text-2xl text-foreground">
                  Portfolio Growth Velocity
                </CardTitle>
                <CardDescription className="text-muted-foreground font-default !w-[330px] ![margin:-2px_0px_-2px_0px] !text-sm">Tracking the cumulative asset value
 managed by Lorenzo Cabason Real Estate.</CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart accessibilityLayer data={chartData} margin={{
                  top: 20,
                  right: 20,
                  left: -20,
                  bottom: 0
                }} className="recharts-surface !w-[330px]">
                    <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.05} />
                    <XAxis dataKey="time" tickLine={false} tickMargin={10} axisLine={false} stroke="currentColor" className="text-muted-foreground font-mono text-xs" />
                    <YAxis tickLine={false} axisLine={false} tickMargin={10} stroke="currentColor" className="text-muted-foreground font-mono text-xs" tickFormatter={value => `$${value}M`} />
                    <ChartTooltip cursor={{
                    fill: "rgba(255,255,255,0.02)"
                  }} content={<ChartTooltipContent className="border-border bg-card text-foreground backdrop-blur-md rounded-md" hideLabel />} />
                    <Bar dataKey="recurrence" fill="var(--color-primary)" radius={[2, 2, 0, 0]} maxBarSize={60}>
                      <LabelList dataKey="recurrence" position="top" offset={12} className="fill-primary font-mono text-sm font-semibold" formatter={(value: number) => `$${value}M`} />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:gap-6">
              {statsData.map((stat, i) => {
              const Icon = stat.icon;
              return <div key={stat.label} data-index={i} className="group flex flex-col justify-between rounded-md border border-border bg-card/30 p-6 shadow-sm backdrop-blur-xl transition-all !w-[330px] hover:bg-card/50">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-mono text-foreground mb-1 text-3xl font-bold tracking-tight">
                        {stat.value}
                      </h3>
                      <p className="font-default text-primary mb-2 text-xs font-bold tracking-widest uppercase">
                        {stat.label}
                      </p>
                      <p className="text-muted-foreground font-default text-xs leading-relaxed">
                        {stat.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button variant="secondary" size="lg" className="px-6 inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-10 has-[>svg]:px-4 font-default rounded-md bg-primary text-primary-foreground ![padding:0px_24px_0px_24px] !m-0 !w-[330px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-secondary/80 hover:opacity-90">
                View Market Insights
              </Button>
              <p className="text-muted-foreground font-default text-xs !w-[330px]">
                *Performance metrics based on historical data and verified 
                transaction records.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}